<template>
  <div class="manage">
    <div v-if="!id" class="card">
      <h2>Gestion des partages</h2>
      <div class="row">
        <input class="email-input" type="email" v-model.trim="email" placeholder="Adresse e-mail"/>
        <button class="btn" :disabled="!validEmail(email) || searching" @click="searchByEmail">Rechercher</button>
      </div>
      <div v-if="searching">Chargement…</div>
      <div v-else-if="shares.length===0 && searched">Aucun partage trouvé.</div>
      <ul class="shares" v-else>
        <li
            v-for="s in shares"
            :key="s.id"
            class="share"
            :class="{ deleted: s.deleted }"
            @click="$router.push({ name:'manage', query:{ id:s.id }})"
        >
          <div class="left">
            <div class="id">
              {{ s.id }}
              <span v-if="s.deleted" class="badge badge-red">Supprimé</span>
            </div>
            <div class="meta">
              Créé: {{ formatDate(s.created_at) }} •
              Expire: {{ s.expires_at ? formatDate(s.expires_at) : '—' }} •
              Rétention: {{ s.retention }}
            </div>
          </div>
          <div class="right">
            <div class="count">{{ s.file_count }} fichier(s)</div>
            <div class="size">{{ formatSize(s.total_size) }}</div>
          </div>
        </li>
      </ul>
    </div>

    <div v-else class="card">
      <div class="head">
        <h2>
          Partage {{ id }}
          <span v-if="meta.deleted" class="badge badge-red">Supprimé</span>
        </h2>
        <button class="btn danger" @click="deleteShare" :disabled="deleting">Supprimer le partage</button>
      </div>

      <div class="grid">
        <div><strong>Créé</strong>
          <div>{{ formatDate(meta.created_at) }}</div>
        </div>
        <div><strong>Expire</strong>
          <div>{{ meta.expires_at ? formatDate(meta.expires_at) : '—' }}</div>
        </div>
        <div><strong>Rétention</strong>
          <div>{{ meta.retention }}</div>
        </div>
        <div><strong>Email</strong>
          <div>{{ meta.email || '—' }}</div>
        </div>
      </div>

      <div class="row mt">
        <select v-model="newRetention" class="retention-select">
          <option value="first_download">jusqu'au premier téléchargement</option>
          <option value="1h">1h</option>
          <option value="1d">1 jour</option>
          <option value="1w">1 semaine</option>
          <option value="1m">1 mois</option>
        </select>
        <button class="btn" @click="extend" :disabled="extending">Prolonger</button>
      </div>

      <h3>Fichiers</h3>
      <ul class="files" v-if="files.length">
        <li v-for="f in files" :key="f.name" class="file" :class="{ deleted: f.deleted }">
          <div class="name" :title="f.name">
            {{ f.name }}
            <span v-if="f.deleted" class="badge badge-red">Supprimé</span>
          </div>
          <div class="size">{{ formatSize(f.size) }}</div>
          <div class="dl">{{ f.downloads }} téléchargements</div>
          <button class="btn" @click="openFile(f)">Détails</button>
        </li>
      </ul>
      <div v-else>Pas de fichiers.</div>
    </div>

    <div v-if="fileModal.visible" class="overlay">
      <div class="modal">
        <div class="modal-head">
          <h3>Détails de {{ fileModal.file?.name }}</h3>
          <button class="btn" @click="fileModal.visible=false">Fermer</button>
        </div>
        <div v-if="eventsLoading">Chargement…</div>
        <div v-else-if="events.length===0">Aucun événement.</div>
        <div class="table-wrap" v-else>
          <table class="table">
            <thead>
            <tr>
              <th>Date</th>
              <th>IP</th>
              <th>Appareil</th>
              <th>Langue</th>
              <th>Référent</th>
              <th>UA</th>
              <th>Timezone</th>
              <th>Écran</th>
              <th>DPR</th>
              <th>Heure locale</th>
              <th>Batterie</th>
              <th>Proto</th>
              <th>Host</th>
              <th>Port</th>
              <th>CH-UA</th>
              <th>CH-Plateforme</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(e,i) in events" :key="i">
              <td>{{ formatDate(e.ts) }}</td>
              <td>{{ e.ip }}</td>
              <td>{{ e.device || '—' }}</td>
              <td>{{ e.accept_language || '—' }}</td>
              <td class="ellipsis" :title="e.referrer">{{ e.referrer || '—' }}</td>
              <td class="ellipsis" :title="e.ua">{{ e.ua }}</td>
              <td>{{ e.timezone || '—' }} ({{ e.tz_offset_min ?? '—' }} min)</td>
              <td>{{ e.screen_width || '—' }}×{{ e.screen_height || '—' }}</td>
              <td>{{ e.dpr ?? '—' }}</td>
              <td>{{ e.local_time || '—' }}</td>
              <td>{{ e.battery_level ?? '—' }}</td>
              <td>{{ e.protocol || '—' }}</td>
              <td>{{ e.host || '—' }}</td>
              <td>{{ e.remote_port ?? '—' }}</td>
              <td class="ellipsis" :title="e.sec_ch_ua">{{ e.sec_ch_ua || '—' }}</td>
              <td>{{ e.sec_ch_platform || '—' }}</td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios'

const API_BASE = process.env.VUE_APP_API_URL || 'http://localhost:3000'

export default {
  name: 'ManageView',

  data() {
    return {
      id: this.$route.query.id || '',
      email: '',
      searching: false,
      searched: false,
      shares: [],
      meta: {created_at: null, expires_at: null, retention: '', email: '', deleted: false},
      files: [],
      newRetention: '1w',
      extending: false,
      deleting: false,
      fileModal: {visible: false, file: null},
      eventsLoading: false,
      events: []
    }
  },

  async mounted() {
    if (this.id) await this.loadShare()
  },

  watch: {
    '$route.query.id': {
      async handler(v) {
        this.id = v || ''
        this.meta = {created_at: null, expires_at: null, retention: '', email: '', deleted: false}
        this.files = []
        this.events = []
        this.fileModal = {visible: false, file: null}
        if (this.id) await this.loadShare()
      }
    }
  },

  methods: {
    validEmail(v) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)
    },

    formatSize(b) {
      if (b < 1024) return b + ' B'
      if (b < 1024 * 1024) return (b / 1024).toFixed(1) + ' KB'
      if (b < 1024 * 1024 * 1024) return (b / (1024 * 1024)).toFixed(1) + ' MB'
      return (b / (1024 * 1024 * 1024)).toFixed(1) + ' GB'
    },

    formatDate(sec) {
      if (!sec) return '—'
      const d = new Date(sec * 1000)
      return d.toLocaleString()
    },

    async searchByEmail() {
      if (!this.validEmail(this.email)) return
      this.searching = true
      this.searched = true
      try {
        const {data} = await axios.get(`${API_BASE}/manage/by-email`, {params: {email: this.email}})
        this.shares = data.shares || []
      } finally {
        this.searching = false
      }
    },

    async loadShare() {
      try {
        const {data} = await axios.get(`${API_BASE}/manage/${encodeURIComponent(this.id)}`)
        this.meta = {
          created_at: data.created_at,
          expires_at: data.expires_at,
          retention: data.retention,
          email: data.email,
          deleted: !!data.deleted
        }
        this.files = data.files || []
        this.newRetention = data.retention || '1w'
      } catch {
        alert('Partage introuvable.')
      }
    },

    async extend() {
      this.extending = true
      try {
        const {data} = await axios.post(`${API_BASE}/manage/${encodeURIComponent(this.id)}/extend`, {retention: this.newRetention})
        this.meta.expires_at = data.expires_at
        this.meta.retention = data.retention
      } finally {
        this.extending = false
      }
    },

    async deleteShare() {
      if (!confirm('Supprimer ce partage ?')) return
      this.deleting = true
      try {
        await axios.delete(`${API_BASE}/manage/${encodeURIComponent(this.id)}`)
        this.$router.push({name: 'manage'})
      } finally {
        this.deleting = false
      }
    },

    async openFile(f) {
      this.fileModal = {visible: true, file: f}
      this.eventsLoading = true
      this.events = []
      try {
        const {data} = await axios.get(`${API_BASE}/manage/${encodeURIComponent(this.id)}/file/${encodeURIComponent(f.name)}/events`)
        this.events = data.events || []
      } finally {
        this.eventsLoading = false
      }
    }
  }
}
</script>

<style scoped>
.manage {
  display: flex;
  justify-content: center;
  padding: 2rem;
}

.card {
  width: min(980px, 95%);
  background: #fff;
  border: 3px dashed var(--gold);
  border-radius: 14px;
  padding: 1.2rem;
  display: grid;
  gap: 1rem;
}

.row {
  display: flex;
  gap: .6rem;
  align-items: center;
}

.mt {
  margin-top: .5rem;
}

.email-input {
  flex: 1;
  max-width: 460px;
  padding: .6rem .8rem;
  border-radius: 10px;
  border: 1px solid var(--gold-16);
  background: #fff;
}

.btn {
  padding: .5rem .9rem;
  border-radius: 8px;
  border: 1px solid var(--gold-16);
  background: #fafafa;
  cursor: pointer;
}

.btn:disabled {
  opacity: .6;
  cursor: not-allowed;
}

.btn.danger {
  border-color: #d8b0b0;
  background: #fff0f0;
}

.shares {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: .6rem;
}

.share {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid var(--gold-16);
  border-radius: 10px;
  padding: .6rem .8rem;
  cursor: pointer;
}

.share:hover {
  background: rgba(0, 0, 0, .03);
}

.share.deleted {
  border-color: #f2c2c2;
  background: #fff6f6;
  color: #9b2c2c;
}

.left .id {
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 560px;
  display: flex;
  align-items: center;
  gap: .4rem;
}

.left .meta {
  opacity: .9;
  font-size: .94rem;
}

.right {
  text-align: right;
  display: grid;
  gap: .2rem;
}

.grid {
  display: grid;
  grid-template-columns:repeat(auto-fit, minmax(180px, 1fr));
  gap: .6rem .8rem;
}

.retention-select {
  padding: .6rem .8rem;
  border-radius: 10px;
  border: 1px solid var(--gold-16);
  background: #fff;
}

.files {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: .5rem;
}

.file {
  display: grid;
  grid-template-columns: 1fr 140px 200px auto;
  align-items: center;
  gap: .6rem;
  border: 1px solid var(--gold-16);
  border-radius: 10px;
  padding: .6rem .8rem;
}

.file.deleted {
  border-color: #f2c2c2;
  background: #fff6f6;
  color: #9b2c2c;
}

.name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.size {
  text-align: right;
}

.dl {
  text-align: right;
}

.badge {
  font-size: .75rem;
  padding: .1rem .4rem;
  border-radius: .5rem;
  border: 1px solid transparent;
}

.badge-red {
  color: #9b2c2c;
  border-color: #f2c2c2;
  background: #fff0f0;
}

.overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, .5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.modal {
  width: min(1200px, 95%);
  background: #fff;
  border-radius: 12px;
  padding: 1rem;
  display: grid;
  gap: .8rem;
}

.modal-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.table-wrap {
  overflow: auto;
  max-height: 70vh;
}

.table {
  width: 100%;
  border-collapse: collapse;
}

.table th, .table td {
  border: 1px solid #eee;
  padding: .5rem;
  font-size: .9rem;
  vertical-align: top;
}

.ellipsis {
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
